import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Squares2X2Icon,
  TruckIcon,
  UserGroupIcon,
  BanknotesIcon,
  ArchiveBoxIcon,
  TagIcon,
  ShieldCheckIcon,
  IdentificationIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  HomeIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { AcademicCapIcon } from '@heroicons/react/24/solid';
import UserContext from '../../../contexts/UserContext';
import { Role } from '../../../utilities/Role';
import { useFlow } from '../../../utilities/useFlow';
import { clearUser } from '../../../services/storage';
import { axiosKK } from '../../../services/networkRequest';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  matchPaths?: string[];
  roles?: Role[];
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [
      {
        to: '/admin/dashboard',
        label: 'Dashboard',
        icon: Squares2X2Icon,
      },
    ],
  },
  {
    label: 'Operate',
    items: [
      {
        to: '/admin/logistics',
        label: 'Logistics',
        icon: TruckIcon,
        matchPaths: [
          '/admin/logistics',
          '/admin/route-plan',
          '/admin/warehouse',
          '/admin/warehouse-forecast',
        ],
        roles: [Role.Admin],
      },
      {
        to: '/admin/partners',
        label: 'Partners',
        icon: UserGroupIcon,
        matchPaths: ['/admin/partners', '/admin/partner-access'],
        roles: [Role.Admin],
      },
      {
        to: '/admin/invoices',
        label: 'Invoices',
        icon: BanknotesIcon,
        roles: [Role.Admin],
      },
    ],
  },
  {
    label: 'Catalogue',
    items: [
      {
        to: '/admin/products',
        label: 'Products',
        icon: ArchiveBoxIcon,
        matchPaths: ['/admin/products', '/admin/product'],
        roles: [Role.Admin],
      },
      {
        to: '/admin/discounts',
        label: 'Discounts',
        icon: TagIcon,
        roles: [Role.Admin],
      },
    ],
  },
  {
    label: 'People',
    items: [
      {
        to: '/admin/customers',
        label: 'Customers',
        icon: IdentificationIcon,
        matchPaths: ['/admin/customers', '/admin/customer'],
      },
      {
        to: '/admin/users',
        label: 'Admins',
        icon: ShieldCheckIcon,
        matchPaths: ['/admin/users', '/admin/user'],
        roles: [Role.Admin],
      },
    ],
  },
];

function isActive(item: NavItem, pathname: string): boolean {
  const candidates = item.matchPaths ?? [item.to];
  return candidates.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
}

function findActiveSection(pathname: string): string | null {
  for (const section of SECTIONS) {
    if (section.items.some((item) => isActive(item, pathname))) {
      return section.label;
    }
  }
  return null;
}

// Inlined Keepr wordmark — same paths as /img/svg/keepr.svg, exposed via
// `currentColor` so the sidebar can theme it from CSS.
function KeeprWordmark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 374 92"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Keepr"
    >
      <path
        fill="currentColor"
        d="M23.4 75H0V0h25.6v54.9l-6.5-5.5L56.4 0h27zm31.8 0L36 40.5l19-14.9L84.4 75zm100.6-22.1h-49V39H141l-6.8 4.7q-.4-3.5-1.9-5.9-1.4-2.4-3.7-3.6-2.3-1.3-5.5-1.3-3.7 0-6.3 1.5-2.5 1.4-3.9 3.9-1.3 2.5-1.3 5.6 0 4.8 2.1 8.1 2.2 3.2 6.6 4.9t11.1 1.7q6.2 0 12.1-1.4t10.9-4v15.4q-6.4 4-14 6.1-7.5 2.1-17 2.1-10.7 0-19.2-3.8-8.4-3.9-13.3-10.8Q86 55.2 86 46q0-9.1 4.7-15.9 4.8-6.8 13-10.6t18.4-3.8q10.7 0 18.2 4.6 7.5 4.5 11.5 12.9 4 8.3 4 19.7zm75 0h-49V39H216l-6.8 4.7q-.4-3.5-1.9-5.9-1.4-2.4-3.7-3.6-2.3-1.3-5.5-1.3-3.7 0-6.3 1.5-2.5 1.4-3.9 3.9-1.3 2.5-1.3 5.6 0 4.8 2.1 8.1 2.2 3.2 6.6 4.9t11.1 1.7q6.2 0 12.1-1.4t10.9-4v15.4q-6.4 4-14 6.1-7.5 2.1-17 2.1-10.7 0-19.2-3.8-8.4-3.9-13.3-10.8-4.9-7-4.9-16.2 0-9.1 4.7-15.9 4.8-6.8 13-10.6t18.4-3.8q10.7 0 18.2 4.6 7.5 4.5 11.5 12.9 4 8.3 4 19.7z"
      />
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M238.3 91.5v-74h25.4v8.8q0 0 0 0t0 0q3.3-4.2 7.8-6.9 6.4-3.7 14.5-3.7 8.5 0 14.8 3.8t9.8 10.7q3.6 6.9 3.6 16.1 0 9.1-3.6 16-3.5 6.9-9.8 10.7T286 76.8q-8.2 0-14.6-3.8-4.4-2.7-7.6-6.8h-.1v25.3zm48.9-51.1q-1.3-2.6-3.8-4.1-2.4-1.5-5.6-1.5-3.3 0-6.4 1.5t-5.4 4.1q-2.2 2.5-3.2 5.6v.5q1 3.2 3.2 5.6 2.3 2.6 5.4 4.1t6.4 1.5q3.2 0 5.6-1.5 2.5-1.5 3.8-4 1.4-2.6 1.4-5.9t-1.4-5.9z"
      />
      <path
        fill="currentColor"
        d="m323 37.5-4-20h26l3.4 20.3V75H323zm50.8-21.2v21.5q-3.4-.6-6.1-.9-2.7-.2-4.8-.2-3.8 0-7.1 1.3-3.3 1.4-5.4 4.9-2 3.5-2 10l-4.5-5.6q1-6.2 2.5-11.9 1.6-5.6 4.2-10.1 2.6-4.4 6.6-7t10=2.6q1.5 0 3.1.1 1.6.2 3.5.5z"
      />
    </svg>
  );
}

// Brand-aware full-width logo for the expanded sidebar. Keepr renders its
// inlined SVG (currentColor-themed). Kit Keeper + St Annes use the same PNG
// asset the customer Header consumes, recoloured solid black via
// `filter: brightness(0)` since the source files are white-on-dark.
function BrandLogo({
  flow,
}: {
  flow: 'Kit Keeper' | 'Keepr' | 'St Annes';
}) {
  if (flow === 'Keepr') {
    return <KeeprWordmark className="admin-sidebar__logo" />;
  }
  if (flow === 'St Annes') {
    return (
      <img
        src="/img/svg/st-annes-fullcolour-WHITE-RGB.svg"
        alt="St Anne's College, Oxford"
        className="admin-sidebar__logo admin-sidebar__logo--invert"
      />
    );
  }
  return (
    <img
      src="/img/Kit Keeper Logo - White.png"
      alt="Kit Keeper"
      className="admin-sidebar__logo admin-sidebar__logo--invert"
    />
  );
}

// Collapsed-mode brand mark — graduation cap. The graduation cap is the
// official Kit Keeper company icon (per brand guidance) and reads as a
// clean app icon at 32 px regardless of which flow is active.
function BrandIcon() {
  return (
    <span className="admin-sidebar__brand-icon" aria-hidden="true">
      <AcademicCapIcon className="admin-sidebar__brand-icon-svg" />
    </span>
  );
}

const COLLAPSED_KEY = 'admin-sidebar-collapsed';

function readCollapsedFromStorage(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsedToStorage(value: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
  } catch {
    // Ignore — Safari private mode etc.
  }
}

function initialsOf(firstName?: string, lastName?: string): string {
  const first = (firstName || '').trim().charAt(0).toUpperCase();
  const last = (lastName || '').trim().charAt(0).toUpperCase();
  if (first && last) return `${first}${last}`;
  if (first) return first;
  return '·';
}

export default function AdminSidebar() {
  const { pathname } = useLocation();
  const history = useHistory();
  const { user } = useContext(UserContext);
  const flow = useFlow();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsedFromStorage);

  // Section open-state: all sections start expanded; user can collapse any.
  // The section containing the active route is always kept expanded so the
  // current page is visible after navigation.
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    () => SECTIONS.reduce((acc, s) => ({ ...acc, [s.label]: true }), {})
  );

  // Keep the active section expanded after route changes — if the user
  // navigates into a section that was collapsed, expand it automatically so
  // the current page is visible.
  useEffect(() => {
    const active = findActiveSection(pathname);
    if (active) {
      setOpenSections((prev) =>
        prev[active] ? prev : { ...prev, [active]: true }
      );
    }
  }, [pathname]);

  // Close drawer on route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  // Persist the collapsed state across reloads.
  useEffect(() => {
    writeCollapsedToStorage(collapsed);
  }, [collapsed]);

  const role = user?.role;

  // Sidebar is admin-portal nav only. Partner-portal users
  // (PartnerMaster / PartnerDriver / PartnerWarehouse) shouldn't see admin
  // nav items — return null and let the shell collapse to a single column.
  if (role !== Role.Admin && role !== Role.Warehouse) {
    return null;
  }

  const visibleSections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => !item.roles || item.roles.includes(role as Role)
    ),
  })).filter((section) => section.items.length > 0);

  const toggleSection = (label: string) => {
    setOpenSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const navigate = (to: string) => {
    history.push(to);
    setDrawerOpen(false);
  };

  const goToBooking = () => {
    setDrawerOpen(false);
    history.push('/');
  };

  const handleLogout = () => {
    clearUser();
    delete axiosKK.defaults.headers.common['Authorization'];
    window.location.assign('/');
  };

  const handleProfile = () => {
    if (!user?.id) return;
    history.push(`/admin/user/${user.id}`);
    setDrawerOpen(false);
  };

  const wrapperClass = [
    'admin-sidebar',
    drawerOpen ? 'admin-sidebar--open' : null,
    collapsed ? 'admin-sidebar--collapsed' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const showWordmark = !collapsed;
  const profileName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
    : '';
  const profileRole = role === Role.Warehouse ? 'Warehouse' : 'Admin';

  return (
    <>
      <button
        type="button"
        className="admin-sidebar__trigger"
        aria-label="Open navigation menu"
        aria-expanded={drawerOpen}
        onClick={() => setDrawerOpen(true)}
      >
        <Bars3Icon
          className="admin-sidebar__trigger-icon"
          aria-hidden="true"
        />
      </button>

      {drawerOpen && (
        <div
          className="admin-sidebar__backdrop"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className={wrapperClass} aria-label="Admin navigation">
        <header className="admin-sidebar__brand">
          <button
            type="button"
            className="admin-sidebar__brand-link"
            onClick={goToBooking}
            aria-label={`${flow} — return to booking`}
            title={`${flow} — return to booking`}
          >
            {showWordmark ? <BrandLogo flow={flow} /> : <BrandIcon />}
          </button>

          <button
            type="button"
            className="admin-sidebar__collapse-toggle"
            aria-label={
              collapsed ? 'Expand sidebar' : 'Collapse sidebar'
            }
            aria-pressed={collapsed}
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? (
              <ChevronDoubleRightIcon
                className="admin-sidebar__collapse-icon"
                aria-hidden="true"
              />
            ) : (
              <ChevronDoubleLeftIcon
                className="admin-sidebar__collapse-icon"
                aria-hidden="true"
              />
            )}
          </button>

          <button
            type="button"
            className="admin-sidebar__close"
            aria-label="Close navigation menu"
            onClick={() => setDrawerOpen(false)}
          >
            <XMarkIcon
              className="admin-sidebar__close-icon"
              aria-hidden="true"
            />
          </button>
        </header>

        <nav className="admin-sidebar__nav">
          {visibleSections.map((section) => {
            const isOpen = openSections[section.label] !== false;
            return (
              <div
                key={section.label}
                className={
                  isOpen
                    ? 'admin-sidebar__group admin-sidebar__group--open'
                    : 'admin-sidebar__group'
                }
              >
                <button
                  type="button"
                  className="admin-sidebar__group-toggle"
                  aria-expanded={isOpen}
                  onClick={() => toggleSection(section.label)}
                >
                  <span className="admin-sidebar__group-label">
                    {section.label}
                  </span>
                  <ChevronDownIcon
                    className="admin-sidebar__group-chevron"
                    aria-hidden="true"
                  />
                </button>
                {(isOpen || collapsed) && (
                  <ul className="admin-sidebar__list">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item, pathname);
                      return (
                        <li
                          key={item.to}
                          className="admin-sidebar__item"
                        >
                          <button
                            type="button"
                            className={
                              active
                                ? 'admin-sidebar__link admin-sidebar__link--active'
                                : 'admin-sidebar__link'
                            }
                            aria-current={active ? 'page' : undefined}
                            title={collapsed ? item.label : undefined}
                            onClick={() => navigate(item.to)}
                          >
                            <Icon
                              className="admin-sidebar__icon"
                              aria-hidden="true"
                            />
                            <span className="admin-sidebar__label">
                              {item.label}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        <footer className="admin-sidebar__footer">
          {user?.id && (
            <button
              type="button"
              className="admin-sidebar__profile"
              onClick={handleProfile}
              title={`${profileName} — open account`}
              aria-label={`${profileName} — open account`}
            >
              <span
                className="admin-sidebar__profile-avatar"
                aria-hidden="true"
              >
                {initialsOf(user.firstName, user.lastName)}
              </span>
              <span className="admin-sidebar__profile-meta">
                <span className="admin-sidebar__profile-name">
                  {profileName}
                </span>
                <span className="admin-sidebar__profile-role">
                  {profileRole}
                </span>
              </span>
            </button>
          )}
          <button
            type="button"
            className="admin-sidebar__footer-link"
            title="Back to booking"
            onClick={goToBooking}
          >
            <HomeIcon
              className="admin-sidebar__footer-icon"
              aria-hidden="true"
            />
            <span className="admin-sidebar__footer-label">
              Back to booking
            </span>
          </button>
          <button
            type="button"
            className="admin-sidebar__footer-link"
            title="Sign out"
            onClick={handleLogout}
          >
            <ArrowRightStartOnRectangleIcon
              className="admin-sidebar__footer-icon"
              aria-hidden="true"
            />
            <span className="admin-sidebar__footer-label">Sign out</span>
          </button>
        </footer>
      </aside>
    </>
  );
}
